import { ContentModuleProps, HeadingProps, PageProps, ParagraphProps } from '~types'
import { SLUG_ACCESSIBILITY, SLUG_FAQS } from '~constants'
import { filterComponentsOfType } from '../utils'

export type QuestionAnswerSDMainEntityType = {
  '@type': string
  name: string
  acceptedAnswer: {
    '@type': string
    text: string
  }
}

export type QuestionAnswerSDType = {
  '@context': string
  '@type': string
  mainEntity: Array<QuestionAnswerSDMainEntityType>
}

const SPHERE_BASE_LOCATION = {
  location: {
    '@type': 'Place',
    name: 'Sphere',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '255 Sands Ave. Las Vegas',
      addressRegion: 'NV',
      postalCode: '89169'
    }
  }
}

export const getEventStructuredData = (
  description: string = '',
  imageUrl: string = '',
  name: string = '',
  performerName: string = '',
  url: string = '',
  price: number | undefined,
  startDate: string = '',
  endDate: string = '',
  eventAttendanceMode: string = 'https://schema.org/OfflineEventAttendanceMode'
) => ({
  '@context': 'http://schema.org',
  '@type': 'Event',
  ...(imageUrl && { image: imageUrl }),
  ...(url && { url: url }),
  ...(name && { name: name }),
  ...(description && { description: description }),
  ...(price && {
    offers: {
      '@type': 'Offer',
      price: price.toString(),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    }
  }),
  ...(startDate && { startDate: startDate }),
  ...(endDate && { endDate: endDate }),
  ...(eventAttendanceMode && { eventAttendanceMode }),
  ...(performerName && {
    performer: {
      '@type': 'Person',
      name: performerName
    }
  }),
  ...SPHERE_BASE_LOCATION
})

/**
 * Creates a dynmaic mapping of Content Heading => Paragraph for Structured Data (CHPSD).
 * @param data
 */
const createDynamicCHPSDMap = (data: PageProps) => {
  const {
    content: {
      body: { blocks }
    }
  } = data

  // @TODO: build this based off of AccordionGroup
  const modules: Array<ContentModuleProps> = filterComponentsOfType<ContentModuleProps>(blocks, 'Module')
  const CHPSDMap: Array<QuestionAnswerSDMainEntityType> = []

  modules.forEach((module: ContentModuleProps) => {
    module?.moduleSections.forEach(moduleSection => {
      const { blocks } = moduleSection
      const question = filterComponentsOfType<HeadingProps>(blocks, 'ContentHeading')
      const answer = filterComponentsOfType<ParagraphProps>(blocks, 'Paragraph')

      if (question[0] && answer[0]) {
        CHPSDMap.push({
          '@type': 'Question',
          name: question[0].title || '',
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer[0]?.text?.html ? answer[0].text.html.replace(/(<([^>]+)>)/gi, '') : ''
          }
        })
      }
    })
  })

  return CHPSDMap
}

export const getStructuredDataBySlug = (slug: string, data?: any) => {
  const dynamicStructuredDataSlugs = [SLUG_ACCESSIBILITY, SLUG_FAQS]

  if (!data) {
    console.warn(`Unable to find seeding data when creating structured data for ${slug}`)
    return {}
  }

  if (dynamicStructuredDataSlugs.indexOf(slug) !== -1) {
    return {
      '@context': 'https://schema.org',
      '@type': (() => {
        if (slug === SLUG_ACCESSIBILITY) return 'AccessibilityServicesPage'
        if (slug === SLUG_FAQS) return 'FAQPage'
        return undefined
      })(),
      mainEntity: createDynamicCHPSDMap(data)
    }
  }

  return null
}
